import React, { useLayoutEffect, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { AppView } from 'design-system/AppView';
import HeaderImage from 'design-system/HeaderImage';
import Button, { HEIGHT } from 'design-system/Button';
import { ButtonGroup } from 'design-system/ButtonGroup';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import links from 'utils/links';
import { TouchableOpacity } from 'react-native-gesture-handler';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { useAppSelector } from 'store/hooks';
import checkFeatureFlag from 'utils/featureFlags';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { isWeb } from '../../../constants';
import RollingBudgetContent from '../components/RollingBudgetContent';
const strings = {
    title: 'Do you want your budgets to roll over?',
    description: 'Any leftover or overspent amount from this period will carry into the next',
    descriptionSubcategories: 'Any leftover or overspent amount from this period will carry into the next. This applies to your total budget and all parent categories, but not to subcategories',
    rollingBudgets: 'Rolling Budgets',
    rollingBudgetsDescription: 'Rolling budgets are a premium feature that makes every budget roll to the next period if they haven’t been filled. This means if you haven’t spent an amount in a cycle, it will be included in the next.',
};
export default withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.ROLLING_BUDGETS}/>),
        });
    }, []);
    const { editedMonthlyBudget, budgetState, shouldResetRollingAmount, hasPrefilledBudgets, } = route.params;
    const navigateToConfirm = useCallback((shouldRollover) => {
        navigation.navigateDeprecated('EditBudget', {
            flow: 'Confirm',
            editedMonthlyBudget,
            shouldRollover,
            budgetState,
            shouldResetRollingAmount,
            period: route.params.period,
            hasPrefilledBudgets,
        });
    }, [
        budgetState,
        editedMonthlyBudget,
        hasPrefilledBudgets,
        route.params.period,
        shouldResetRollingAmount,
    ]);
    const onPress = useCallback(async () => {
        navigateToConfirm(false);
    }, [navigateToConfirm]);
    const onPressYes = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        navigateToConfirm(true);
    }, [navigateToConfirm]), 'rollingBudgets');
    const onLearnMore = () => links.openKnownLink('rollingBudgets');
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT * 2 + rem(16), paddingHorizontalStyle);
    const showSubcategoriesCreateFlow = useAppSelector((store) => checkFeatureFlag(store.utils.featureFlags, 'show_subcategories_create_flow'));
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={paddingBottom} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title={strings.title} subTitle={<>
                <Text TextThin-16 Gray>
                  {showSubcategoriesCreateFlow
                ? strings.descriptionSubcategories
                : strings.description}
                </Text>
                <Spacer h={8}/>
                <View style={{
                alignSelf: 'flex-start',
            }}>
                  <TouchableOpacity onPress={onLearnMore}>
                    <Text Text-16 Primary>
                      How do rolling budgets work?
                    </Text>
                  </TouchableOpacity>
                </View>
              </>}/>

          <View style={styles.contentContainer}>
            <HeaderImage style={styles.headerImgBg} sourceComponent={<RollingBudgetContent showDescription={false}/>} imgStyle={styles.headerImage} onDark/>
          </View>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button brandReversed title="Yes, rollover" onPress={onPressYes}/>
          <Button brand title="No, start fresh" onPress={onPress}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    pageTitle: isWeb ? 'Set Rollover' : undefined,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    contentContainer: {
        paddingHorizontal: rem(16),
    },
    headerImgBg: {
        height: undefined,
    },
    headerImage: {
        width: '100%',
        aspectRatio: 1,
        height: undefined,
    },
});
